import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Lock, Users, Trophy } from "lucide-react";
import { LotteryInfo } from "@/lib/contract";

interface DrawCardProps {
  lottery: LotteryInfo;
  isWalletConnected: boolean;
  isParticipant: boolean;
  onParticipate: () => void;
  onDrawWinner?: () => void;
  onDecryptWinner?: () => void;
  canDraw: boolean;
  canDecrypt: boolean;
}

export const DrawCard = ({ 
  lottery,
  isWalletConnected,
  isParticipant,
  onParticipate,
  onDrawWinner,
  onDecryptWinner,
  canDraw,
  canDecrypt,
}: DrawCardProps) => {
  const handleClick = () => {
    if (lottery.isActive && isWalletConnected && !isParticipant) {
      onParticipate();
    }
  };

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 border-2 border-border/50 hover:border-primary/30 bg-card/80 backdrop-blur-sm overflow-hidden">
      <div className="absolute inset-0 bg-magic-gradient opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
      
      <CardHeader className="relative">
        <div className="flex items-start justify-between">
          <div className="space-y-2 flex-1">
            <CardTitle className="text-2xl flex items-center gap-2">
              {lottery.name}
              <Lock className="h-4 w-4 text-muted-foreground" />
            </CardTitle>
            <CardDescription>Lottery ID: {lottery.id.toString()}</CardDescription>
          </div>
          <Badge 
            variant={lottery.isActive ? "default" : "secondary"}
            className={lottery.isActive ? "bg-primary text-primary-foreground" : ""}
          >
            {lottery.isActive ? "Active" : lottery.isDrawn ? "Drawn" : "Closed"}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4 relative">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>{lottery.participantCount.toString()} / {lottery.maxParticipants.toString()}</span>
          </div>
          {lottery.isDrawn && lottery.winner && (
            <div className="flex items-center gap-2 text-accent">
              <Trophy className="h-4 w-4" />
              <span className="text-xs">Winner Selected</span>
            </div>
          )}
        </div>
        
        <div className="pt-4 border-t border-border/50 space-y-2">
          {lottery.isActive && !lottery.isDrawn && (
            <Button 
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
              disabled={!isWalletConnected || isParticipant || Number(lottery.participantCount) >= Number(lottery.maxParticipants)}
              onClick={handleClick}
            >
              {!isWalletConnected 
                ? "Connect Wallet" 
                : isParticipant 
                ? "Already Participated" 
                : Number(lottery.participantCount) >= Number(lottery.maxParticipants)
                ? "Lottery Full"
                : "Participate"}
            </Button>
          )}
          
          {lottery.isActive && !lottery.isDrawn && canDraw && Number(lottery.participantCount) > 0 && (
            <Button 
              variant="outline"
              className="w-full"
              onClick={onDrawWinner}
            >
              Draw Winner
            </Button>
          )}
          
          {lottery.isDrawn && canDecrypt && (
            <Button 
              variant="outline"
              className="w-full"
              onClick={onDecryptWinner}
            >
              Decrypt Winner Number
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
